/**
 * BatterySaver — Minimal survival-mode UI overlay.
 * Shows only critical info: nearest resources, SOS, navigation.
 * High contrast, no animations, reduced network calls.
 */
import { useState, useEffect } from 'react';
import { X, Navigation, MapPin, Flame, Droplets, Zap, Heart, Home, Phone, AlertTriangle, Battery, BatteryLow } from 'lucide-react';

export const BatterySaver = ({ active, onClose, userLocation, resources, onNavigate, onSOS, c1 }) => {
  const [batteryLevel, setBatteryLevel] = useState(null);

  useEffect(() => {
    if (!active) return;
    const getBattery = async () => {
      try {
        if (navigator.getBattery) {
          const batt = await navigator.getBattery();
          setBatteryLevel(Math.round(batt.level * 100));
          const update = () => setBatteryLevel(Math.round(batt.level * 100));
          batt.addEventListener('levelchange', update);
          return () => batt.removeEventListener('levelchange', update);
        }
      } catch { /* not supported */ }
    };
    getBattery();
  }, [active]);

  if (!active) return null;

  // Find nearest resources by category
  const getNearest = (category) => {
    if (!userLocation || !resources?.length) return null;
    const filtered = resources.filter(r => r.category === category && r.lat && r.lng);
    if (!filtered.length) return null;
    let best = null, bestDist = Infinity;
    for (const r of filtered) {
      const d = Math.sqrt(Math.pow(r.lat - userLocation.lat, 2) + Math.pow(r.lng - userLocation.lng, 2));
      if (d < bestDist) { bestDist = d; best = r; }
    }
    return best;
  };

  const essentialCategories = [
    { cat: 'water', label: 'Water', icon: Droplets, color: '#3b82f6' },
    { cat: 'food', label: 'Food', icon: Home, color: '#22c55e' },
    { cat: 'shelter', label: 'Shelter', icon: Home, color: '#8b5cf6' },
    { cat: 'medical', label: 'Medical', icon: Heart, color: '#ec4899' },
    { cat: 'charging', label: 'Charge', icon: Zap, color: '#eab308' },
    { cat: 'fuel', label: 'Fuel', icon: Flame, color: '#f97316' },
  ];

  return (
    <div className="fixed inset-0 z-[1100] flex flex-col" style={{ backgroundColor: '#0a0a0a' }} data-testid="battery-saver-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <BatteryLow className="w-5 h-5 text-amber-400" />
          <span className="text-sm font-bold text-white">SURVIVAL MODE</span>
          {batteryLevel !== null && (
            <span className={`text-xs font-mono ${batteryLevel < 20 ? 'text-red-400' : 'text-green-400'}`}>{batteryLevel}%</span>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="battery-saver-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* SOS Button */}
        <button
          onClick={onSOS}
          className="w-full py-4 rounded-xl text-lg font-black text-white flex items-center justify-center gap-3"
          style={{ backgroundColor: '#dc2626' }}
          data-testid="battery-saver-sos"
        >
          <AlertTriangle className="w-6 h-6" /> SOS EMERGENCY
        </button>

        {/* Nearest Resources */}
        <div>
          <p className="text-xs text-gray-500 font-semibold mb-2 uppercase tracking-wider">Nearest Resources</p>
          <div className="grid grid-cols-2 gap-2">
            {essentialCategories.map(({ cat, label, icon: Icon, color }) => {
              const nearest = getNearest(cat);
              return (
                <button
                  key={cat}
                  onClick={() => nearest && onNavigate({ lat: nearest.lat, lng: nearest.lng, name: nearest.name })}
                  disabled={!nearest}
                  className="flex items-start gap-2 p-3 rounded-lg border border-gray-800 text-left transition-colors disabled:opacity-30"
                  style={{ backgroundColor: '#111' }}
                  data-testid={`battery-nearest-${cat}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{label}</p>
                    <p className="text-[10px] text-gray-500 truncate">{nearest ? nearest.name : 'None nearby'}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Emergency number */}
        <a
          href="tel:999"
          className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 border border-gray-700"
          style={{ backgroundColor: '#1a1a1a' }}
          data-testid="battery-saver-call-999"
        >
          <Phone className="w-4 h-4" /> Call 999
        </a>

        {/* Minimal info */}
        <div className="text-center pt-2">
          <p className="text-[10px] text-gray-600">Animations & background sync disabled to conserve battery</p>
          <p className="text-[10px] text-gray-600">Tap a resource to navigate</p>
        </div>
      </div>
    </div>
  );
};
